// ABOUTME: Public pricing page displaying subscription tiers for Oleum BI.
// ABOUTME: Fetches public plans from the API and renders pricing cards with CTAs.

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, Zap, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface PublicPlan {
  name: string;
  display_name: string;
  description: string;
  is_self_service: boolean;
  price_monthly_cents: number;
  sort_order: number;
  limits: {
    max_users: number;
    max_data_sources: number;
    max_reports_per_month: number;
    max_messages_per_month: number;
    max_active_investigations: number;
  };
}

/** Format a limit value for display. -1 means unlimited. */
function formatLimit(value: number, label: string): string {
  if (value === -1) return `Unlimited ${label}`;
  return `${value} ${label}`;
}

function formatPrice(cents: number): { amount: string; suffix?: string } {
  if (cents === 0) return { amount: '$0', suffix: '/month' };
  return { amount: `$${(cents / 100).toFixed(0)}`, suffix: '/month' };
}

function buildFeatures(plan: PublicPlan): string[] {
  const features: string[] = [];
  features.push(formatLimit(plan.limits.max_messages_per_month, 'messages/mo'));
  features.push(formatLimit(plan.limits.max_active_investigations, 'investigations'));
  features.push(formatLimit(plan.limits.max_data_sources, 'data sources'));
  features.push(formatLimit(plan.limits.max_reports_per_month, 'reports/mo'));
  if (plan.limits.max_users > 1) {
    features.push(formatLimit(plan.limits.max_users, 'team members'));
  }
  return features;
}

function getCtaForPlan(plan: PublicPlan): { label: string; action: 'navigate' | 'link'; target: string } {
  if (plan.is_self_service && plan.price_monthly_cents === 0) {
    return { label: 'Get Started', action: 'navigate', target: '/signup' };
  }
  if (plan.is_self_service) {
    return { label: 'Start Free Trial', action: 'navigate', target: `/signup?plan=${plan.name}` };
  }
  return { label: 'Contact Sales', action: 'link', target: 'mailto:sales@oleum.pro?subject=Enterprise%20Inquiry' };
}

export function PricingPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<PublicPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadPlans() {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/plans`);
        if (!response.ok) throw new Error('Failed to load plans');
        const data: PublicPlan[] = await response.json();
        setPlans(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load pricing');
      } finally {
        setLoading(false);
      }
    }
    loadPlans();
  }, []);

  // Pick one self-service paid plan to highlight (the first one by sort order)
  const highlightedPlan = plans.find(p => p.is_self_service && p.price_monthly_cents > 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-surface-50 to-surface-100">
      {/* Navigation */}
      <nav className="flex items-center justify-between px-6 py-4 max-w-7xl mx-auto">
        <Link to="/" className="text-surface-600 hover:text-surface-900 transition-colors">
          Back to Home
        </Link>
        <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium transition-colors">
          Log In
        </Link>
      </nav>

      {/* Header */}
      <div className="text-center pt-12 pb-16 px-6">
        <h1 className="text-4xl md:text-5xl font-bold text-surface-900 mb-4">
          Simple, Transparent Pricing
        </h1>
        <p className="text-lg text-surface-600 max-w-2xl mx-auto">
          Choose the plan that fits your needs. Start free and upgrade as you grow.
        </p>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-7xl mx-auto px-6 pb-20">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          </div>
        ) : error ? (
          <div className="text-center text-error py-12">{error}</div>
        ) : (
          <div className={`grid grid-cols-1 gap-8 ${
            plans.length === 1 ? 'md:grid-cols-1 max-w-md mx-auto' :
            plans.length === 2 ? 'md:grid-cols-2 max-w-3xl mx-auto' :
            'md:grid-cols-3'
          }`}>
            {plans.map((plan) => {
              const isHighlighted = plan.name === highlightedPlan?.name;
              const price = formatPrice(plan.price_monthly_cents);
              const features = buildFeatures(plan);
              const cta = getCtaForPlan(plan);

              return (
                <Card
                  key={plan.name}
                  variant={isHighlighted ? 'surface' : 'glass'}
                  overflowVisible={isHighlighted}
                  className={`relative flex flex-col ${
                    isHighlighted
                      ? 'ring-2 ring-primary-500 shadow-lg scale-105'
                      : 'hover:shadow-md transition-shadow'
                  }`}
                >
                  {/* Badge */}
                  {isHighlighted && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary-500 text-white text-xs font-semibold rounded-full">
                        <Zap className="w-3 h-3" />
                        Most Popular
                      </span>
                    </div>
                  )}

                  {/* Tier Name */}
                  <h3 className="text-xl font-semibold text-surface-900 mb-2">
                    {plan.display_name}
                  </h3>

                  {/* Price */}
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-surface-900">{price.amount}</span>
                    {price.suffix && (
                      <span className="text-surface-500">{price.suffix}</span>
                    )}
                  </div>

                  {/* Description */}
                  <p className="text-surface-600 mb-6">{plan.description}</p>

                  {/* Features */}
                  <ul className="space-y-3 mb-8 flex-grow">
                    {features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                        <span className="text-surface-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  {cta.action === 'link' ? (
                    <a
                      href={cta.target}
                      className={`btn btn-md w-full justify-center ${
                        isHighlighted ? 'btn-primary' : 'btn-secondary'
                      }`}
                    >
                      {cta.label}
                    </a>
                  ) : (
                    <Button
                      variant={isHighlighted ? 'primary' : 'secondary'}
                      className="w-full justify-center"
                      onClick={() => navigate(cta.target)}
                    >
                      {cta.label}
                    </Button>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* FAQ */}
      <div className="bg-surface-0 border-t border-surface-200 py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-semibold text-surface-900 mb-4">
            Questions?
          </h2>
          <p className="text-surface-600 mb-6">
            Need help choosing the right plan? Our team is here to help.
          </p>
          <a
            href="mailto:support@oleum.pro"
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
}
